import React, { useEffect, useMemo, useState } from 'react';
import { authService } from '../../../utils/authService';
import { MaterialReactTable, useMaterialReactTable, MRT_ColumnDef, MRT_Row } from 'material-react-table';
import { Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

interface Bug {
  StudentId: string;
  firstName: string;
  issue_type: string;
  date_of_issue_reported: string;
  date_of_issue_resolved: string | null;
  resolution_score: string;
  college: string;
  branch: string;
}

const Bugsoverviewpage: React.FC = () => {
  const [bugs, setBugs] = useState<Bug[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filteredBugs, setFilteredBugs] = useState<Bug[]>([]);
  const [selectedColleges, setSelectedColleges] = useState<string[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [isAllCollegesSelected, setIsAllCollegesSelected] = useState<boolean>(false);
  const location=useLocation();
  const navigate = useNavigate();

  const fetchBugs = async () => {
    try {
      const bugsResponse = await authService.get('/admin/tickets/');
      setBugs(bugsResponse.data);
      setFilteredBugs(bugsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBugs();
  }, []);

  useEffect(() => {
    const filtered = bugs.filter(
      (bug) =>
        (selectedColleges.length === 0 || selectedColleges.includes(bug.college)) &&
        (selectedBranches.length === 0 || selectedBranches.includes(bug.branch))
    );
    setFilteredBugs(filtered);
  }, [selectedColleges, selectedBranches, bugs]);

  const handleViewClick = (studentId: string) => {
    if (location.pathname.includes('admin')) {
      navigate('/admin/tickets/ticket', { state: { student_id: studentId }});
    }
    else if (location.pathname.includes('trainer')) {
      navigate('/trainer/tickets/ticket', { state: { student_id: studentId }});
    }
    else{
      navigate("/");
    }
  };

  const columns = useMemo<MRT_ColumnDef<Bug>[]>(
    () => [
      {
        accessorKey: 'StudentId',
        header: 'Student ID',
      },
      {
        accessorKey: 'firstName',
        header: 'Student Name',
      },
      {
        accessorKey: 'issue_type',
        header: 'Issue Type',
      },
      {
        accessorKey: 'date_of_issue_reported',
        header: 'Reported Date',
         Cell: ({ cell }) => {
          const date = new Date(cell.getValue<string>());
          return date.toLocaleDateString('en-GB', {
            timeZone: 'UTC',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        }
      },
      {
        accessorKey: 'date_of_issue_resolved',
        header: 'Solved Date',
        Cell: ({ cell }) =>
          cell.getValue<string | null>() ? new Date(cell.getValue<string>()).toLocaleDateString() : 'Not resolved',
      },
      {
        accessorKey: 'resolution_score',
        header: 'Status',
        Cell: ({ cell }) => (
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {cell.getValue<string>()}
          </Box>
        ),
      },
      {
        id: 'view',
        header: 'Action',
        Cell: ({ row }: { row: MRT_Row<Bug> }) => (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleViewClick(row.original.StudentId)}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredBugs,
    state: { isLoading: loading },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    enablePagination: true,
    enableSorting: true,
    muiTablePaperProps: {
      sx: {
        borderRadius: '10px',
      },
    },
    muiTableContainerProps: { sx: { height: 'calc(100vh - 225px)' } },
    muiTableBodyRowProps: { hover: true },
    initialState: {
      pagination: { pageSize: 15, pageIndex: 0 },
      sorting: [{ id: 'date_of_issue_reported', desc: true }],
      density: 'compact',
    },
  });

  return (
    <div style={{ marginTop: '1%', marginRight: '0.5%' }}>
      <MaterialReactTable table={table} />
    </div>
  );
};

export default Bugsoverviewpage;
